import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getBrowserFingerprint } from "@/lib/fingerprint";
import { readAssetDetailRpc } from "@/lib/parse-asset-detail-from-rpc";
import { queryKeys } from "@/lib/query-keys";

export interface AssetDetail {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  price: number;
  is_free: boolean;
  status: string;
  version: string;
  download_count: number;
  view_count: number;
  featured: boolean;
  video_url: string | null;
  license_type: string | null;
  custom_license: string | null;
  file_size: number | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  sale_price: number | null;
  sale_starts_at: string | null;
  sale_ends_at: string | null;
  rejection_reason: string | null;
  pricing_type: "fixed" | "pwyw" | "free";
  pwyw_minimum: number | null;
  tipping_enabled: boolean;
  visibility: "public" | "unlisted" | "private";
  scheduled_publish_at: string | null;
  purchases_last_24h: number;
  purchases_last_7d: number;
  purchases_last_30d: number;
  // Nullable: the `get_asset_detail` RPC returns `seller: null` when the
  // seller profile row is gone (deleted account) — every consumer must guard.
  seller: {
    id: string;
    user_id: string;
    display_name: string | null;
    avatar_url: string | null;
    username: string | null;
    bio: string | null;
    is_seller: boolean;
    seller_verified: boolean;
    social_youtube: string | null;
    social_twitter: string | null;
    social_discord: string | null;
    social_tiktok: string | null;
    social_instagram: string | null;
  } | null;
  category: {
    id: string;
    name: string;
    slug: string;
    color: string | null;
  } | null;
  images: {
    id: string;
    image_url: string;
    is_primary: boolean;
    display_order: number;
  }[];
  tags: string[];
  avg_rating: number | null;
  review_count: number;
}

/**
 * One listing's detail as the signed-in caller may see it, or `null` when there
 * is no such listing or it isn't theirs to see (draft, in review, private…) —
 * `get_asset_detail` decides that server-side, from the caller's session.
 */
export async function fetchAssetDetail(slug: string): Promise<AssetDetail | null> {
  const { data, error } = await supabase.rpc("get_asset_detail", { p_slug: slug });
  if (error) throw error;
  const lookup = readAssetDetailRpc(data);
  return lookup.kind === "visible" ? lookup.asset : null;
}

export function useAssetDetail(slug: string) {
  const viewCountedRef = useRef<string | null>(null);

  const query = useQuery({
    queryKey: queryKeys.assets.detail(slug),
    queryFn: () => fetchAssetDetail(slug),
    enabled: !!slug,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });

  // Increment view count once per asset per session using atomic RPC
  useEffect(() => {
    const assetId = query.data?.id;
    if (!assetId || viewCountedRef.current === assetId) return;

    // Check if this asset was already viewed in this session
    const viewedAssetsKey = "uefnmarket_viewed_assets";
    const viewedAssets: string[] = JSON.parse(sessionStorage.getItem(viewedAssetsKey) || "[]");
    
    if (viewedAssets.includes(assetId)) {
      viewCountedRef.current = assetId;
      return;
    }

    // Mark as counted immediately to prevent duplicate calls
    viewCountedRef.current = assetId;

    // Increment view count atomically using RPC with fingerprint dedup
    const incrementView = async () => {
      try {
        const fingerprint = await getBrowserFingerprint();
        await supabase.rpc("increment_view_count_safe", { p_asset_id: assetId, p_fingerprint: fingerprint });
        
        // Mark as viewed in session
        viewedAssets.push(assetId);
        sessionStorage.setItem(viewedAssetsKey, JSON.stringify(viewedAssets));
      } catch (error) {
        console.error("Failed to increment view count:", error);
      }
    };

    incrementView();
  }, [query.data?.id]);

  return query;
}
